import { defineComponent as _defineComponent } from 'vue';
import { vModelText as _vModelText, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "number-plate-input" };
const _hoisted_2 = { class: "number-plate-input__container" };
const _hoisted_3 = ["disabled"];
import { ref, computed, nextTick } from 'vue';
import { useOilFinder } from '../../composables/useOilFinder';
import { useVehicleTypes } from '../../composables/useVehicleTypes';
import { useVehicleDetails } from '../../composables/useVehicleDetails';
import { useAssets } from '../../composables/useAssets';
const countryCode = 'GB'; // Fixed to GB
export default /*@__PURE__*/ _defineComponent({
    __name: 'NumberPlateInput',
    props: {
        isOverlay: { type: Boolean }
    },
    emits: ["searchStart", "searchComplete"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const { assetUrl } = useAssets();
        // Props for overlay mode
        const props = __props;
        // Emits for overlay mode
        const emit = __emit;
        const { api } = useOilFinder();
        const { vehicleTypes, setVehicleTypes, setLoading, setError } = useVehicleTypes();
        const { licencePlate, setLicencePlate } = useVehicleDetails();
        const overlayLicencePlate = ref('');
        const inputValue = computed({
            get: () => (props.isOverlay ? overlayLicencePlate.value : licencePlate.value),
            set: (value) => {
                if (props.isOverlay) {
                    overlayLicencePlate.value = value;
                    return;
                }
                licencePlate.value = value;
            },
        });
        // Add ref for the input element
        const inputRef = ref(null);
        // Expose focus method for parent component
        const focusInput = () => {
            inputRef.value?.focus();
            // Select all text so user can immediately type to replace
            inputRef.value?.select();
        };
        __expose({
            focusInput
        });
        // Computed property to check if we should show edit mode
        // Don't show edit mode in overlay
        const showEditMode = computed(() => {
            if (props.isOverlay)
                return false;
            return vehicleTypes.value && vehicleTypes.value.length > 0;
        });
        // Function to enable editing
        const enableEditing = () => {
            // Clear the vehicle types to return to search mode
            setVehicleTypes(null);
            // Clear the license plate
            setLicencePlate('');
            // Clear any errors
            setError('');
            // Focus the input field
            nextTick(() => {
                const input = document.querySelector('.number-plate-input__field');
                if (input) {
                    input.disabled = false;
                    input.focus();
                }
            });
        };
        // Scroll to results function
        const scrollToVehicleTypes = () => {
            setTimeout(() => {
                // Try multiple selectors to find the vehicle types section
                const selectors = [
                    '.vehicle-types',
                    '[data-vehicle-types]',
                    '.vehicle-types-section',
                    '.vehicle-types-container',
                    '.vehicle-type-list',
                    '[data-vehicle-type-list]'
                ];
                let vehicleTypesSection = null;
                let targetElement = null;
                // First try to find by known selectors
                for (const selector of selectors) {
                    vehicleTypesSection = document.querySelector(selector);
                    if (vehicleTypesSection) {
                        // Try to find the outermost container/wrapper
                        const parent = vehicleTypesSection.parentElement;
                        if (parent && (parent.classList.contains('vehicle-types-wrapper') ||
                            parent.classList.contains('container') ||
                            parent.classList.contains('section'))) {
                            targetElement = parent;
                        }
                        else {
                            targetElement = vehicleTypesSection;
                        }
                        break;
                    }
                }
                if (targetElement) {
                    // Offset to show more space above the section
                    const offset = 120;
                    const elementPosition = targetElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
                else {
                    // Fallback: scroll down by a fixed amount
                    window.scrollBy({
                        top: 500,
                        behavior: 'smooth'
                    });
                }
            }, 200); // Delay to ensure DOM has fully updated
        };
        async function searchByNumberPlate() {
            const valueToSearch = inputValue.value.trim();
            if (!valueToSearch) {
                setError('Please enter a vehicle registration number');
                return;
            }
            // Emit search-start for loading spinner in overlay mode
            if (props.isOverlay) {
                emit('searchStart', valueToSearch);
            }
            setLoading(true);
            if (!props.isOverlay) {
                setLicencePlate(valueToSearch);
            }
            const response = await api.getVehicleTypesByLicencePlate(countryCode, valueToSearch);
            if (response.success && response.data) {
                // If in overlay mode, emit the results to parent
                if (props.isOverlay) {
                    emit('searchComplete', response.data);
                    setLoading(false);
                    return;
                }
                // Normal mode - set vehicle types and scroll
                setVehicleTypes(response.data);
                await nextTick();
                setTimeout(() => {
                    scrollToVehicleTypes();
                }, 300);
            }
            else {
                setError(response.error || 'Failed to find vehicle');
            }
            setLoading(false);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _withDirectives(_createElementVNode("input", {
                        ref_key: "inputRef",
                        ref: inputRef,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((inputValue).value = $event)),
                        type: "text",
                        placeholder: "ENTER REG",
                        class: "number-plate-input__field",
                        maxlength: "8",
                        disabled: showEditMode.value,
                        onKeyup: _withKeys(searchByNumberPlate, ["enter"])
                    }, null, 40, _hoisted_3), [
                        [_vModelText, inputValue.value]
                    ]),
                    (!showEditMode.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            onClick: searchByNumberPlate,
                            class: "number-plate-input__search"
                        }, [...(_cache[1] || (_cache[1] = [
                                _createElementVNode("svg", {
                                    width: "65",
                                    height: "65",
                                    viewBox: "0 0 65 65",
                                    fill: "none",
                                    xmlns: "http://www.w3.org/2000/svg"
                                }, [
                                    _createElementVNode("circle", {
                                        cx: "29.7918",
                                        cy: "29.7917",
                                        r: "18.9583",
                                        stroke: "currentColor",
                                        "stroke-width": "5"
                                    }),
                                    _createElementVNode("path", {
                                        d: "M54.1665 54.1667L46.0415 46.0417",
                                        stroke: "currentColor",
                                        "stroke-width": "5",
                                        "stroke-linecap": "round"
                                    })
                                ], -1)
                            ]))]))
                        : (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            onClick: enableEditing,
                            class: "number-plate-input__edit"
                        }, [...(_cache[2] || (_cache[2] = [
                                _createElementVNode("svg", {
                                    viewBox: "0 0 28 32",
                                    fill: "none",
                                    xmlns: "http://www.w3.org/2000/svg"
                                }, [
                                    _createElementVNode("path", {
                                        d: "M12.5944 5.33331H4.57989C3.97259 5.33331 3.39015 5.61426 2.96072 6.11436C2.53129 6.61446 2.29004 7.29273 2.29004 7.99998V26.6666C2.29004 27.3739 2.53129 28.0522 2.96072 28.5523C3.39015 29.0524 3.97259 29.3333 4.57989 29.3333H20.6089C21.2162 29.3333 21.7986 29.0524 22.2281 28.5523C22.6575 28.0522 22.8987 27.3739 22.8987 26.6666V17.3333M21.1813 3.33331C21.6368 2.80288 22.2546 2.50488 22.8987 2.50488C23.5429 2.50488 24.1606 2.80288 24.6161 3.33331C25.0716 3.86374 25.3275 4.58316 25.3275 5.33331C25.3275 6.08346 25.0716 6.80288 24.6161 7.33331L13.7393 20L9.1596 21.3333L10.3045 16L21.1813 3.33331Z",
                                        stroke: "currentColor",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                    })
                                ], -1),
                                _createElementVNode("span", null, "Edit", -1)
                            ]))]))
                ])
            ]));
        };
    }
});
